import { dateRangeFromISO, getDateRange } from "@app/lib/date-range";
import { getPerspectiveId } from "@app/lib/perspective";
import StakeholdersCatPage, {
  PAGE_SIZE,
  stakeholdersCatQuery,
} from "@app/pages/stakeholders-cat/stakeholders-cat-page";
import type { stakeholdersCatPage_Query } from "@app/queries/stakeholdersCatPage_Query.graphql";
import { environment } from "@app/relay/relay-environment";
import { pageHead } from "@app/lib/page-title";
import { createFileRoute } from "@tanstack/react-router";
import { Suspense } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { loadQuery } from "react-relay";

// Optional, and omitted entirely when off: a required search param would force
// every existing <Link to="/stakeholders-cat"> in the app to pass one.
type StakeholdersCatSearch = { uncategorized?: boolean };

// ?uncategorized=1 is the /data-health "Categorize these" link: the checklist
// counts a number and this lands on exactly the rows behind it. Accepts the
// boolean too so the toggle can round-trip its own state through the URL.
export const Route = createFileRoute("/_authed/stakeholders-cat")({
  validateSearch: (search: Record<string, unknown>): StakeholdersCatSearch =>
    search.uncategorized === true || search.uncategorized === "1"
      ? { uncategorized: true }
      : {},
  loaderDeps: ({ search }) => ({ uncategorized: search.uncategorized === true }),
  loader: ({ deps }) =>
    loadQuery<stakeholdersCatPage_Query>(
      environment,
      stakeholdersCatQuery,
      {
        perspectiveId: getPerspectiveId() ?? undefined,
        from: dateRangeFromISO(getDateRange()) ?? undefined,
        search: null,
        uncategorized: deps.uncategorized,
        limit: PAGE_SIZE,
        offset: 0,
      },
      { fetchPolicy: "network-only" }
    ),
  head: () => pageHead("Stakeholders by cost"),
  component: StakeholdersCatRoute,
});

function StakeholdersCatRoute() {
  const queryRef = Route.useLoaderData();
  const { uncategorized } = Route.useSearch();

  return (
    <ErrorBoundary
      fallback={
        <div className="p-6 text-red-500">Error loading stakeholders</div>
      }
    >
      <Suspense
        fallback={
          <div className="p-6 text-gray-500">Loading stakeholders…</div>
        }
      >
        <StakeholdersCatPage queryRef={queryRef} uncategorizedOnly={uncategorized} />
      </Suspense>
    </ErrorBoundary>
  );
}
